import React, { FC, useState } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';

const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace" };
const C = { card:'#131720',border:'#1E2330',gold:'#D4A017',teal:'#19E6CF',
  white:'#F0F4FF',ink:'#C8D0E0',dim:'#6B7590',green:'#22C55E',red:'#EF4444' };

interface Player {
  id: string;
  name: string;
  buyIn: number;
  rebuys: number;
  addOns: number;
  finishPosition?: number;
  eliminatedAt?: number;
}

interface SeatingLogEntry {
  generatedAt: string;
  mode: 'random' | 'manual';
  playersSeated: number;
  tableCount: number;
}

interface PostEventReportProps {
  eventId: string;
  eventName: string;
  venueData?: any;
  players: Player[];
  prizePool: number;
  charityPool: number;
  charityName?: string;
  payouts: { position: string; amount: string }[];
  buyIn: number;
  levelIdx: number;
  elapsed: number;
  jackpots?: { label: string; paidAmount: number; winner: string }[];
  seatingMode?: 'random' | 'manual';
  seatingLog?: SeatingLogEntry[];
  onClose: () => void;
}

const fmt = (s: number) => {
  const h = Math.floor(s/3600);
  const m = Math.floor((s%3600)/60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const PostEventReport: FC<PostEventReportProps> = ({
  eventId, eventName, venueData, players, prizePool, charityPool,
  charityName, payouts, buyIn, levelIdx, elapsed, jackpots,
  seatingMode, seatingLog, onClose
}) => {
  const [printing, setPrinting] = useState(false);

  const totalEntries = players.length;
  const rebuys = players.reduce((a, p) => a + p.rebuys, 0);
  const addOns = players.reduce((a, p) => a + p.addOns, 0);
  const totalCollected = prizePool + charityPool;
  const finishers = [...players].filter(p => p.finishPosition)
    .sort((a, b) => (a.finishPosition||0) - (b.finishPosition||0));
  const winner = finishers[0];
  const primary = venueData?.brand_color_primary ?? '#19E6CF';

  const getPayoutForPosition = (pos: number) => {
    const entry = payouts[pos - 1];
    if (!entry) return null;
    if (entry.amount.includes('%')) {
      return `$${Math.floor(prizePool * parseFloat(entry.amount) / 100).toLocaleString()}`;
    }
    return entry.amount;
  };

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => { window.print(); setPrinting(false); }, 300);
  };

  return (
    <Box position="fixed" inset={0} bg="rgba(0,0,0,0.85)" zIndex={1000}
      display="flex" alignItems="center" justifyContent="center" p={4}>

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #post-event-report, #post-event-report * { visibility: visible !important; }
          #post-event-report {
            position: fixed !important; top: 0 !important; left: 0 !important;
            width: 100vw !important; background: white !important; color: #000 !important;
          }
        }
      `}</style>

      <Box id="post-event-report" bg={C.card} borderRadius={12}
        border={`1px solid ${C.border}`} w="100%" maxW="680px"
        maxH="90vh" overflowY="auto">

        {/* Header */}
        <Box p={5} borderBottom={`1px solid ${C.border}`}
          style={{ background:`linear-gradient(135deg, #080A0E, ${primary}18)` }}>
          <Flex justify="space-between" align="flex-start">
            <Box>
              <Text style={{ fontFamily:F.mono, fontSize:10, color:primary,
                letterSpacing:'0.2em', marginBottom:4 }}>POST-EVENT REPORT</Text>
              <Text style={{ fontFamily:F.ui, fontSize:22, fontWeight:900,
                color:C.white, lineHeight:1.1 }}>{eventName}</Text>
              {venueData?.name && (
                <Text style={{ fontFamily:F.ui, fontSize:14, color:C.dim, marginTop:2 }}>
                  {venueData.name}
                </Text>
              )}
              <Text style={{ fontFamily:F.mono, fontSize:11, color:C.dim, marginTop:4 }}>
                {new Date().toLocaleDateString('en-CA', { weekday:'long', year:'numeric',
                  month:'long', day:'numeric' })}
              </Text>
            </Box>
            <Flex gap={2}>
              <Box as="button" px={3} py={2} bg="#0D1F1E"
                border={`1px solid ${C.teal}`} borderRadius={6}
                fontSize="12px" fontWeight="700" color={C.teal}
                fontFamily={F.ui} cursor="pointer"
                onClick={handlePrint}>
                🖨 Print
              </Box>
              <Box as="button" px={3} py={2} bg={C.card}
                border={`1px solid ${C.border}`} borderRadius={6}
                fontSize="12px" color={C.dim} fontFamily={F.ui} cursor="pointer"
                onClick={onClose}>✕</Box>
            </Flex>
          </Flex>
        </Box>

        <Box p={5}>

          {/* Summary stats */}
          <Flex gap={3} flexWrap="wrap" mb={5}>
            {[
              { label:'ENTRIES', value:totalEntries, color:C.teal },
              { label:'REBUYS', value:rebuys, color:C.gold },
              { label:'ADD-ONS', value:addOns, color:C.gold },
              { label:'PLAYER POOL', value:`$${prizePool.toLocaleString()}`, color:C.green },
              ...(charityPool > 0 ? [{ label:`${charityName||'CHARITY'}`, value:`$${charityPool.toLocaleString()}`, color:C.red }] : []),
              { label:'TOTAL COLLECTED', value:`$${totalCollected.toLocaleString()}`, color:C.white },
              { label:'GAME LENGTH', value:fmt(elapsed), color:C.dim },
              { label:'LEVELS PLAYED', value:levelIdx + 1, color:C.dim },
            ].map(s => (
              <Box key={s.label} bg="#0D1118" border={`1px solid ${C.border}`}
                borderRadius={8} p={3} textAlign="center" minW="100px" flex={1}>
                <Text style={{ fontFamily:F.ui, fontSize:22, fontWeight:800,
                  color:s.color, lineHeight:1 }}>{s.value}</Text>
                <Text style={{ fontFamily:F.mono, fontSize:9, color:C.dim,
                  letterSpacing:'0.12em', marginTop:4 }}>{s.label}</Text>
              </Box>
            ))}
          </Flex>

          {/* Winner highlight */}
          {winner && (
            <Box bg="#1A1000" border={`2px solid ${C.gold}`} borderRadius={10}
              p={4} mb={4} textAlign="center">
              <Text style={{ fontFamily:F.mono, fontSize:11, color:C.gold,
                letterSpacing:'0.2em', marginBottom:8 }}>🏆 WINNER</Text>
              <Text style={{ fontFamily:F.ui, fontSize:28, fontWeight:900,
                color:C.white, marginBottom:4 }}>{winner.name}</Text>
              <Text style={{ fontFamily:F.ui, fontSize:20, fontWeight:700,
                color:C.gold }}>
                {getPayoutForPosition(1) || 'Prize TBD'}
              </Text>
            </Box>
          )}

          {/* Results table */}
          {finishers.length > 0 && (
            <Box mb={4}>
              <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim,
                letterSpacing:'0.15em', marginBottom:8 }}>FINAL RESULTS</Text>
              <Box bg="#0D1118" border={`1px solid ${C.border}`} borderRadius={8}
                overflow="hidden">
                {finishers.slice(0, 10).map((p, i) => (
                  <Flex key={p.id} px={4} py={2} align="center"
                    bg={i === 0 ? '#1A1000' : i % 2 === 0 ? '#0A0A0A' : 'transparent'}
                    borderBottom={`1px solid ${C.border}`}>
                    <Text style={{ fontFamily:F.mono, fontSize:14, fontWeight:700,
                      color: i === 0 ? C.gold : C.dim,
                      width:'32px', flexShrink:0 }}>{p.finishPosition}.</Text>
                    <Text style={{ fontFamily:F.ui, fontSize:15, fontWeight:i===0?800:600,
                      color:i===0?C.white:C.ink, flex:1 }}>{p.name}</Text>
                    <Flex gap={2} align="center">
                      {p.rebuys > 0 && (
                        <Text style={{ fontFamily:F.mono, fontSize:10,
                          color:C.gold }}>{p.rebuys}R</Text>
                      )}
                      {p.addOns > 0 && (
                        <Text style={{ fontFamily:F.mono, fontSize:10,
                          color:C.teal }}>+A</Text>
                      )}
                      <Text style={{ fontFamily:F.mono, fontSize:14, fontWeight:700,
                        color:i < (payouts.length) ? C.green : C.dim, minW:'60px',
                        textAlign:'right' }}>
                        {getPayoutForPosition(p.finishPosition!) || '—'}
                      </Text>
                    </Flex>
                  </Flex>
                ))}
                {finishers.length > 10 && (
                  <Box px={4} py={2} textAlign="center">
                    <Text style={{ fontFamily:F.ui, fontSize:12, color:C.dim }}>
                      +{finishers.length - 10} more players
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Jackpots */}
          {jackpots && jackpots.length > 0 && (
            <Box mb={4}>
              <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim,
                letterSpacing:'0.15em', marginBottom:8 }}>JACKPOTS PAID</Text>
              <Box bg="#0D1118" border={`1px solid ${C.border}`} borderRadius={8}
                overflow="hidden">
                {jackpots.map((j, i) => (
                  <Flex key={i} px={4} py={2} align="center"
                    borderBottom={`1px solid ${C.border}`}>
                    <Box flex={1}>
                      <Text style={{ fontFamily:F.ui, fontSize:14, color:C.white }}>{j.label}</Text>
                      {j.winner && (
                        <Text style={{ fontFamily:F.ui, fontSize:12, color:C.dim }}>
                          Won by {j.winner}
                        </Text>
                      )}
                    </Box>
                    <Text style={{ fontFamily:F.mono, fontSize:16, fontWeight:700,
                      color:C.gold }}>${j.paidAmount.toFixed(2)}</Text>
                  </Flex>
                ))}
              </Box>
            </Box>
          )}

          {/* Charity summary */}
          {charityPool > 0 && (
            <Box bg="#0A1000" border={`1px solid #166534`} borderRadius={8} p={4} mb={4}>
              <Text style={{ fontFamily:F.mono, fontSize:10, color:C.green,
                letterSpacing:'0.15em', marginBottom:8 }}>❤️ CHARITABLE CONTRIBUTION</Text>
              <Flex justify="space-between" align="center">
                <Box>
                  <Text style={{ fontFamily:F.ui, fontSize:15, fontWeight:700,
                    color:C.white }}>{charityName || 'Charity'}</Text>
                  <Text style={{ fontFamily:F.ui, fontSize:12, color:C.dim, marginTop:2 }}>
                    Contribution from this event
                  </Text>
                </Box>
                <Text style={{ fontFamily:F.ui, fontSize:26, fontWeight:900,
                  color:C.green }}>${charityPool.toLocaleString()}</Text>
              </Flex>
            </Box>
          )}

          {/* Seating */}
          {seatingMode && (
            <Box bg="#0D1118" border={`1px solid ${C.border}`} borderRadius={8} p={4} mb={4}>
              <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim,
                letterSpacing:'0.15em', marginBottom:8 }}>SEATING</Text>
              <Text style={{ fontFamily:F.ui, fontSize:14, color:C.white, marginBottom:4 }}>
                {seatingMode === 'random'
                  ? 'Seat and table assignments were generated using a cryptographically secure randomization algorithm.'
                  : 'Seat and table assignments were made manually by the Tournament Director.'}
              </Text>
              {seatingLog && seatingLog.length > 0 && (
                <Box mt={2}>
                  {seatingLog.map((entry, i) => (
                    <Text key={i} style={{ fontFamily:F.mono, fontSize:11, color:C.dim, marginBottom:2 }}>
                      {new Date(entry.generatedAt).toLocaleString('en-CA', {
                        month:'short', day:'numeric', hour:'2-digit', minute:'2-digit'
                      })} — {entry.mode === 'random'
                        ? `${entry.playersSeated} player(s) seated across ${entry.tableCount} table(s)`
                        : 'Manual seating mode selected'}
                    </Text>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Financial summary */}
          <Box bg="#0D1118" border={`1px solid ${C.border}`} borderRadius={8}
            overflow="hidden" mb={4}>
            <Box px={4} py={3} bg="#0A0A0A" borderBottom={`1px solid ${C.border}`}>
              <Text style={{ fontFamily:F.mono, fontSize:10, color:C.dim,
                letterSpacing:'0.15em' }}>FINANCIAL SUMMARY</Text>
            </Box>
            {[
              { label:`${totalEntries} entries × $${buyIn}`, value:`$${(totalEntries * buyIn).toLocaleString()}` },
              ...(rebuys > 0 ? [{ label:`${rebuys} rebuys × $${buyIn}`, value:`$${(rebuys * buyIn).toLocaleString()}` }] : []),
              ...(addOns > 0 ? [{ label:`${addOns} add-ons`, value:`$${(addOns * buyIn).toLocaleString()}` }] : []),
              { label:'Total Collected', value:`$${totalCollected.toLocaleString()}`, bold:true },
              ...(charityPool > 0 ? [{ label:`${charityName || 'Charity'} (${Math.round(charityPool/totalCollected*100)}%)`, value:`- $${charityPool.toLocaleString()}`, color:C.red }] : []),
              { label:'Player Prize Pool', value:`$${prizePool.toLocaleString()}`, color:C.green, bold:true },
            ].map((row: any, i) => (
              <Flex key={i} px={4} py={2} justify="space-between"
                bg={i % 2 === 0 ? '#0A0A0A' : 'transparent'}
                borderBottom={`1px solid ${C.border}`}>
                <Text style={{ fontFamily:F.ui, fontSize:14, color:C.dim }}>{row.label}</Text>
                <Text style={{ fontFamily:row.bold ? F.ui : F.mono, fontSize:14,
                  fontWeight:row.bold ? 800 : 400,
                  color:row.color || (row.bold ? C.white : C.ink) }}>{row.value}</Text>
              </Flex>
            ))}
          </Box>

          {/* Powered by */}
          <Flex align="center" justify="center" gap={2} pt={2}>
            <img src="/favicon.gif" alt="PokerCity"
              style={{ width:8, height:14, objectFit:'contain' }} />
            <Text style={{ fontFamily:F.mono, fontSize:9, color:'#2A3040',
              letterSpacing:'0.12em' }}>POWERED BY POKERCITY.COM</Text>
          </Flex>
        </Box>
      </Box>
    </Box>
  );
};

export default PostEventReport;
